import { computed, effect, inject, Injectable, signal } from '@angular/core';
import {
  EstablishmentV2,
  EstablishmentsSignatoriesServiceV2,
  EstablishmentsSignatoryV2,
} from 'api-client';
import { LogService } from 'shared';

import { EstablishmentStore } from '../../establishment/store/establishment.store';

/**
 * Represents a group of missing fields in a specific category.
 * Maps directly to wizard steps in Phase 37.
 */
export interface MissingFieldGroup {
  category: 'business' | 'contact' | 'address' | 'banking' | 'signatories' | 'contract';
  fields: string[];
}

/**
 * OnboardingStore — Completeness-check signal store.
 *
 * Determines whether an establishment has all required data:
 * - 11 required fields on EstablishmentV2
 * - At least one signatory record
 * - Signed contract (signedContractUrl non-null)
 *
 * Consumed by:
 * - Phase 35 guard: reads isComplete() to block/allow dashboard
 * - Phase 37 wizard: reads missingFields() to show which steps need attention
 */
@Injectable({ providedIn: 'root' })
export class OnboardingStore {
  private readonly establishmentStore = inject(EstablishmentStore);
  private readonly signatoriesService = inject(EstablishmentsSignatoriesServiceV2);
  private readonly logService = inject(LogService);

  // Private mutable signals
  private readonly _signatories = signal<EstablishmentsSignatoryV2[]>([]);
  private readonly _signatoriesLoading = signal(false);
  private readonly _signatoriesLoaded = signal(false);

  // Public readonly signals
  readonly signatories = this._signatories.asReadonly();
  readonly signatoriesLoading = this._signatoriesLoading.asReadonly();

  /**
   * Whether all data sources are still loading.
   * Combines EstablishmentStore loading state with signatories loading state.
   */
  readonly isLoading = computed(
    () =>
      this.establishmentStore.isLoading() ||
      this.establishmentStore.isLoadingDetails() ||
      this._signatoriesLoading(),
  );

  /**
   * Missing fields grouped by category.
   * Returns empty array when no establishment details are loaded.
   * Each group maps to a wizard step in Phase 37.
   */
  readonly missingFields = computed((): MissingFieldGroup[] => {
    const details = this.establishmentStore.currentEstablishmentDetails();
    if (!details) return [];

    const groups: MissingFieldGroup[] = [];

    // Business info (wizard step 1)
    const businessMissing = this.checkBusinessFields(details);
    if (businessMissing.length > 0) {
      groups.push({ category: 'business', fields: businessMissing });
    }

    // Contact info (wizard step 2)
    const contactMissing = this.checkContactFields(details);
    if (contactMissing.length > 0) {
      groups.push({ category: 'contact', fields: contactMissing });
    }

    // Address info (wizard step 3)
    const addressMissing = this.checkAddressFields(details);
    if (addressMissing.length > 0) {
      groups.push({ category: 'address', fields: addressMissing });
    }

    // Banking info (wizard step 4)
    const bankingMissing = this.checkBankingFields(details);
    if (bankingMissing.length > 0) {
      groups.push({ category: 'banking', fields: bankingMissing });
    }

    // Signatories (wizard step 5)
    if (this._signatories().length === 0) {
      groups.push({ category: 'signatories', fields: ['signatory'] });
    }

    // Contract (wizard step 6 — after data entry)
    if (!details.signedContractUrl) {
      groups.push({ category: 'contract', fields: ['signedContractUrl'] });
    }

    return groups;
  });

  /**
   * Whether the establishment has all required data.
   *
   * Returns:
   * - `undefined` while data is still loading (indeterminate)
   * - `true` when all fields, signatories, and contract are present
   * - `false` when any required data is missing
   *
   * CRITICAL: Returns undefined (not false) during loading to prevent
   * false negatives and flash redirects in guards.
   */
  readonly isComplete = computed((): boolean | undefined => {
    // While any data is still loading, return undefined (indeterminate)
    if (this.isLoading()) return undefined;

    const details = this.establishmentStore.currentEstablishmentDetails();

    // No establishment loaded yet — indeterminate
    if (!details) return undefined;

    // Signatories haven't been loaded yet — indeterminate
    if (!this._signatoriesLoaded()) return undefined;

    // All data loaded — check completeness
    return this.missingFields().length === 0;
  });

  constructor() {
    // Auto-fetch signatories when establishment changes
    effect(() => {
      const estId = this.establishmentStore.currentEstablishmentId();
      if (estId) {
        this.logService.debug('OnboardingStore', 'Establishment changed, loading signatories', {
          establishmentId: estId,
        });
        this.loadSignatories(estId);
      } else {
        // Reset signatories state when no establishment is selected
        this._signatories.set([]);
        this._signatoriesLoaded.set(false);
      }
    });
  }

  /**
   * Fetch signatories for the given establishment.
   * On error, sets signatories to empty array (incomplete = safe fallback).
   */
  private loadSignatories(establishmentId: number): void {
    this._signatoriesLoading.set(true);
    this._signatoriesLoaded.set(false);

    this.signatoriesService
      .apiV2EstablishmentsSignatoriesEstablishmentEstablishmentIdGet(establishmentId)
      .subscribe({
        next: (signatories: EstablishmentsSignatoryV2[]) => {
          this._signatories.set(signatories);
          this._signatoriesLoading.set(false);
          this._signatoriesLoaded.set(true);
          this.logService.debug('OnboardingStore', 'Signatories loaded', {
            count: signatories.length,
          });
        },
        error: (err: unknown) => {
          this.logService.error('OnboardingStore', 'Failed to load signatories', err);
          this._signatories.set([]);
          this._signatoriesLoading.set(false);
          this._signatoriesLoaded.set(true); // Mark as loaded even on error — empty = incomplete
        },
      });
  }

  /**
   * Manually reload signatories for the current establishment.
   * Called after signatory CRUD operations in the wizard.
   */
  reloadSignatories(): void {
    const estId = this.establishmentStore.currentEstablishmentId();
    if (estId) {
      this.loadSignatories(estId);
    }
  }

  // --- Field check helpers ---

  private checkBusinessFields(details: EstablishmentV2): string[] {
    const missing: string[] = [];
    if (!details.establismentName) missing.push('establismentName');
    if (!details.countryId) missing.push('countryId');
    return missing;
  }

  private checkContactFields(details: EstablishmentV2): string[] {
    const missing: string[] = [];
    if (!details.emailAddress) missing.push('emailAddress');
    if (!details.phoneNumber) missing.push('phoneNumber');
    return missing;
  }

  private checkAddressFields(details: EstablishmentV2): string[] {
    const missing: string[] = [];
    if (!details.address) missing.push('address');
    if (!details.city) missing.push('city');
    if (!details.state) missing.push('state');
    if (!details.zipCode) missing.push('zipCode');
    if (!details.vatNumber) missing.push('vatNumber');
    return missing;
  }

  private checkBankingFields(details: EstablishmentV2): string[] {
    const missing: string[] = [];
    if (!details.bankName) missing.push('bankName');
    if (!details.iban) missing.push('iban');
    if (!details.swiftCode) missing.push('swiftCode');
    return missing;
  }
}
